/**
 * FieldControl.tsx — renders the correct control per field type.
 * Matches view_editor.jsx FieldControl exactly.
 */

import React from 'react';
import { View, Text, TouchableOpacity, Platform, ViewStyle } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Input, Textarea, AppDropdown, Switch, Icon } from '../../components/ui';
import type { FieldDef } from '../../data/mockData';
import LocalizedInput from './localizedInput';

interface FieldControlProps {
  field: FieldDef;
  value: any;
  locale?: string;
  onChange: (v: any) => void;
  /**
   * SKCATALOG-CC-002 CR-2 (P02) — render read-only instead of editable.
   *
   * Set when a brand-locked field is being viewed at LOCATION scope. Handled as a
   * single short-circuit below rather than threaded through all eleven type
   * branches: the read-only presentation is the same for every type (the value,
   * a lock glyph, and the helper text), and adding a `disabled` prop to each
   * control would be eleven chances to miss one — which would leave a field that
   * looks editable and 403s on save.
   */
  readOnly?: boolean;
  /** Why it is read-only — shown under the value. DB-001 §2: "Set by brand — locked." */
  readOnlyHint?: string;
}

export default function FieldControl({
  field,
  value,
  locale,
  onChange,
  readOnly,
  readOnlyHint,
}: FieldControlProps) {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const ft = field.type;

  // Pre-select the first option for ENUM fields when nothing is chosen yet, so
  // the dropdown shows a real value initially instead of an empty placeholder.
  React.useEffect(() => {
    if (ft === 'ENUM' && (value === undefined || value === null || value === '') && field.options && field.options.length > 0) {
      onChange(field.options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ft, value, field.options]);

  // ── CC-002 CR-2 · locked at location scope: read-only, with the reason ──
  //
  // Before every editable branch, so nothing downstream can accidentally render
  // an input for a field the API will refuse to write.
  if (readOnly) {
    return (
      <View style={{ gap: 6 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            minHeight: 38,
            paddingHorizontal: 12,
            borderWidth: 1,
            // Dashed, so "you cannot type here" is legible without relying on
            // the (subtle) difference between a disabled and an enabled input.
            borderStyle: 'dashed',
            borderColor: colors.line,
            borderRadius: radii.r2,
            backgroundColor: colors.paper2,
            width: 200,
            maxWidth: '100%',
            alignSelf: 'flex-start',
          }}
        >
          <Icon name="lock" size={12} color={colors.ink4} />
          <Text
            numberOfLines={1}
            style={{
              flex: 1,
              // Mono + LTR for values, per the suite spec — and it keeps a
              // locked price aligned with the editable ones above it.
              fontFamily: ft === 'CURRENCY' || ft === 'NUMBER' ? font.mono : font.sans,
              writingDirection: 'ltr',
              fontSize: fontSize.md,
              color: colors.ink2,
            }}
          >
            {formatReadOnly(ft, value)}
          </Text>
        </View>
        {!!readOnlyHint && (
          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
            {readOnlyHint}
          </Text>
        )}
      </View>
    );
  }

  // TEXT / RICH_TEXT
  if (ft === 'TEXT' || ft === 'RICH_TEXT') {
    if (field.localized) {
      return (
        <LocalizedInput
          field={field}
          value={value}
          rich={ft === 'RICH_TEXT'}
          onChange={onChange}
        />
      );
    }
    if (ft === 'RICH_TEXT') {
      return (
        <Textarea
          value={value || ''}
          onChangeText={onChange}
          placeholder={`Add ${field.label.toLowerCase()}…`}
        />
      );
    }
    return (
      <Input
        value={value || ''}
        onChangeText={onChange}
        placeholder={`Add ${field.label.toLowerCase()}…`}
      />
    );
  }

  // CURRENCY — ₹ prefix, mono font. Compact width — a price doesn't need a
  // full-width box. (CommonInput doesn't render a left icon, so the ₹ is an
  // overlay sitting in the small left pad instead of an empty gap.)
  if (ft === 'CURRENCY') {
    return (
      <View style={{ position: 'relative', width: 200, maxWidth: '100%', alignSelf: 'flex-start' }}>
        <View
          pointerEvents="none"
          style={{ position: 'absolute', left: 12, top: 0, bottom: 0, justifyContent: 'center', zIndex: 1 }}
        >
          <Text style={{ color: colors.ink3, fontSize: fontSize.md, fontFamily: font.mono }}>₹</Text>
        </View>
        <Input
          keyboardType="numeric"
          value={value != null ? String(value) : ''}
          onChangeText={v => onChange(Number(v))}
          inputStyle={{ fontFamily: font.mono, paddingLeft: 22 }}
        />
      </View>
    );
  }

  // NUMBER — optional unit suffix. Compact width like CURRENCY.
  if (ft === 'NUMBER') {
    return (
      <View style={{ position: 'relative', width: 200, maxWidth: '100%', alignSelf: 'flex-start' }}>
        <Input
          keyboardType="numeric"
          value={value != null ? String(value) : ''}
          onChangeText={v => onChange(Number(v))}
          inputStyle={{ fontFamily: font.mono, paddingRight: field.unit ? 40 : 0 }}
          trailingIcon={
            field.unit ? (
              <Text style={{
                color: colors.ink4,
                fontSize: fontSize.sm,
                fontFamily: font.mono,
              }}>
                {field.unit}
              </Text>
            ) : undefined
          }
        />
      </View>
    );
  }

  // BOOLEAN
  if (ft === 'BOOLEAN') {
    return (
      <Switch
        checked={!!value}
        onChange={checked => onChange(checked)}
        label={value ? 'Yes' : 'No'}
      />
    );
  }

  // ENUM
  if (ft === 'ENUM') {
    const opts = [{ value: '', label: '—' }, ...(field.options || []).map(o => ({ value: o, label: o }))];
    return (
      <AppDropdown
        placeholder="Select…"
        value={value || ''}
        onSelect={v => onChange(v)}
        items={opts}
        isDark={isDark}
        compact
        fieldStyle={{ maxWidth: 300 }}
      />
    );
  }

  // MULTI_SELECT — premium checkbox chips (tick any number of options).
  if (ft === 'MULTI_SELECT') {
    const sel: string[] = Array.isArray(value) ? value : [];
    const options = field.options || [];
    if (options.length === 0) {
      return (
        <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>
          No options yet — add one below.
        </Text>
      );
    }
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {options.map(o => {
          const active = sel.includes(o);
          return (
            <TouchableOpacity
              key={o}
              activeOpacity={0.85}
              onPress={() => onChange(active ? sel.filter(x => x !== o) : [...sel, o])}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingVertical: 8,
                paddingHorizontal: 12,
                borderRadius: radii.r2,
                borderWidth: 1,
                borderColor: active ? colors.accent : colors.line,
                backgroundColor: active ? colors.accentSoft : colors.card,
                ...Platform.select({
                  web: { cursor: 'pointer', transition: 'all 120ms ease', userSelect: 'none' } as ViewStyle,
                  default: {},
                }),
              }}
            >
              {/* checkbox square */}
              <View
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 5,
                  borderWidth: active ? 0 : 1.5,
                  borderColor: colors.lineStrong,
                  backgroundColor: active ? colors.accent : 'transparent',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {active && <Icon name="check" size={12} color={colors.white} />}
              </View>
              <Text style={{
                fontSize: fontSize.sm,
                fontWeight: active ? (fontWeight.medium as any) : '400',
                color: active ? colors.accentInk : colors.ink2,
                fontFamily: font.sans,
              }}>
                {o}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  }

  // DATE — native date picker on web; YYYY-MM-DD text fallback elsewhere
  if (ft === 'DATE') {
    const dateStr = typeof value === 'string' ? value.slice(0, 10) : '';
    if (Platform.OS === 'web') {
      return (
        // eslint-disable-next-line react-native/no-raw-text
        <input
          type="date"
          value={dateStr}
          onChange={(e: any) => onChange(e.target.value)}
          style={{
            height: 38,
            border: `1px solid ${colors.line}`,
            borderRadius: 8,
            padding: '0 12px',
            fontSize: 14,
            fontFamily: font.sans,
            color: colors.ink,
            background: colors.card,
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box',
          } as any}
        />
      );
    }
    return (
      <Input
        value={dateStr}
        onChangeText={onChange}
        placeholder="YYYY-MM-DD"
        inputStyle={{ fontFamily: font.mono }}
      />
    );
  }

  // GEO — latitude / longitude pair stored as "lat, lng"
  if (ft === 'GEO') {
    return (
      <Input
        value={value || ''}
        onChangeText={onChange}
        placeholder="lat, lng  (e.g. 13.0827, 80.2707)"
        inputStyle={{ fontFamily: font.mono }}
        leadingIcon={<Icon name="map-pin" size={14} color={colors.ink3} />}
      />
    );
  }

  // JSON — structured value, multiline
  if (ft === 'JSON') {
    return (
      <Textarea
        value={typeof value === 'string' ? value : value != null ? JSON.stringify(value, null, 2) : ''}
        onChangeText={onChange}
        placeholder='{ "key": "value" }'
        inputStyle={{ fontFamily: font.mono } as any}
      />
    );
  }

  // RELATION — id of a related item (picker is a future enhancement)
  if (ft === 'RELATION') {
    return (
      <Input
        value={value || ''}
        onChangeText={onChange}
        placeholder="Related item ID"
        inputStyle={{ fontFamily: font.mono }}
        leadingIcon={<Icon name="link" size={14} color={colors.ink3} />}
      />
    );
  }

  // MEDIA
  if (ft === 'MEDIA') {
    return (
      <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>
        Managed in the Media section below ↓
      </Text>
    );
  }

  // default
  return (
    <Input
      value={value || ''}
      onChangeText={onChange}
    />
  );
}

// ── Helpers ──
function filterPillBase(theme: any): ViewStyle {
  return {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 30,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: theme.colors.line,
    borderRadius: theme.radii.r2,
    backgroundColor: theme.colors.card,
    ...Platform.select({
      web: { cursor: 'pointer', userSelect: 'none' } as ViewStyle,
      default: {},
    }),
  };
}

function filterPillText(theme: any) {
  return {
    fontSize: theme.fontSize.sm,
    color: theme.colors.ink2,
    fontFamily: theme.font.sans,
    fontWeight: theme.fontWeight?.medium ?? '500',
  };
}

/**
 * Display form of a value in the read-only (locked) row.
 *
 * Kept deliberately plain — no currency symbol, no grouping. The locked row
 * shows what the brand SET, and dressing it up would invite the reader to
 * compare it against a formatted editable price above and wonder why they look
 * different. `—` for an unset value, because "the brand locked this and left it
 * empty" is a real state worth showing rather than an empty box.
 */
function formatReadOnly(fieldType: string, value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  if (fieldType === 'BOOLEAN') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') {
    // Localized fields are a BCP-47 map; show the first entry rather than JSON.
    const first = Object.keys(value).sort()[0];
    return first ? String((value as Record<string, unknown>)[first] ?? '—') : '—';
  }
  return String(value);
}
